import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Badge,
  Paper,
} from "@mui/material";
import {
  Inventory,
  Warning,
  CheckCircle,
  Error,
  TrendingUp,
  TrendingDown,
  Add,
  Edit,
  Visibility,
  LocalDining,
  Kitchen,
  RestaurantMenu,
  Grain,
  Liquor,
  Fastfood,
  EmojiFoodBeverage,
  LocalBar,
  Scale,
  Schedule,
  LocationOn,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// Enhanced pantry type configurations
const pantryConfig = {
  GRAIN: {
    icon: Grain,
    color: "#8bc34a",
    gradient: "linear-gradient(135deg, #8bc34a, #689f38)",
    category: "Dry Goods",
    bgPattern: "🌾",
  },
  SPICE: {
    icon: Fastfood,
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    category: "Seasonings",
    bgPattern: "🌶️",
  },
  OIL: {
    icon: Liquor,
    color: "#ffc107",
    gradient: "linear-gradient(135deg, #ffc107, #ff8f00)",
    category: "Liquids",
    bgPattern: "🛢️",
  },
  PASTE: {
    icon: LocalBar,
    color: "#e91e63",
    gradient: "linear-gradient(135deg, #e91e63, #c2185b)",
    category: "Sauces",
    bgPattern: "🍯",
  },
  MANUALBOWL: {
    icon: EmojiFoodBeverage,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    category: "Utensils",
    bgPattern: "🥣",
  },
  MANUALCUP: {
    icon: LocalDining,
    color: "#3f51b5",
    gradient: "linear-gradient(135deg, #3f51b5, #303f9f)",
    category: "Utensils",
    bgPattern: "☕",
  },
};

// Status helper function
const getBoxStatus = (filledPercentage) => {
  if (filledPercentage === 0) {
    return {
      status: "EMPTY",
      color: "#f44336",
      label: "Empty",
      icon: Error,
      severity: "error",
      message: "Box is empty",
    };
  } else if (filledPercentage <= 15) {
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "error",
      message: "Urgent refill needed",
    };
  } else if (filledPercentage <= 35) {
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: TrendingDown,
      severity: "warning",
      message: "Consider refilling soon",
    };
  } else {
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "Well Stocked",
      icon: CheckCircle,
      severity: "success",
      message: "Stock level is healthy",
    };
  }
};

const PantryBox = ({ data, onClick }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [imageError, setImageError] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Calculate fill percentage and status
  const filledPercentage = data?.box_capacity
    ? Math.round((data.box_filled / data.box_capacity) * 100)
    : 0;

  const config = pantryConfig[data?.inventory_type] || pantryConfig.GRAIN;
  const status = getBoxStatus(filledPercentage);
  const IconComponent = config.icon;
  const StatusIcon = status.icon;

  // Format last modified date
  const getLastModified = () => {
    if (!data?.modified) return "Unknown";
    const date = new Date(parseInt(data.modified));
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setShowActions(true)}
      onHoverEnd={() => setShowActions(false)}
    >
      <Card
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          height: "100%",
          minHeight: 420,
          position: "relative",
          cursor: "pointer",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}90 100%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}90 100%)`,
          border: `1px solid ${theme.palette.divider}30`,
          backdropFilter: "blur(20px)",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 20px 60px rgba(0,0,0,0.4), 0 0 30px ${config.color}40`
                : `0 20px 60px rgba(0,0,0,0.15), 0 0 30px ${config.color}40`,
            border: `1px solid ${config.color}60`,
            "& .pantry-glow": {
              opacity: 0.08,
            },
            "& .status-indicator": {
              transform: "scale(1.1) rotate(5deg)",
            },
            "& .card-content": {
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper}98 0%, ${theme.palette.grey[900]}95 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper}98 0%, ${theme.palette.grey[50]}95 100%)`,
            },
          },
        }}
      >
        {/* Animated Background Glow */}
        <Box
          className="pantry-glow"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: config.gradient,
            opacity: 0,
            transition: "opacity 0.3s ease",
            zIndex: 1,
          }}
        />

        {/* Status Indicator Strip */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: status.color,
              boxShadow: `0 0 20px ${status.color}60`,
              zIndex: 2,
            }}
          />
        </motion.div>

        {/* Fill Percentage Badge */}
        <Badge
          badgeContent={`${filledPercentage}%`}
          color={
            status.severity === "error"
              ? "error"
              : status.severity === "warning"
              ? "warning"
              : "success"
          }
          sx={{
            position: "absolute",
            top: 18,
            right: 28,
            zIndex: 3,
            "& .MuiBadge-badge": {
              fontSize: "0.75rem",
              fontWeight: 700,
              height: 28,
              minWidth: 48,
              borderRadius: 4,
              boxShadow: `0 2px 8px ${status.color}40`,
            },
          }}
        >
          <motion.div
            className="status-indicator"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: status.color,
                boxShadow: `0 4px 16px ${status.color}40`,
              }}
            >
              <StatusIcon sx={{ color: "white", fontSize: 24 }} />
            </Avatar>
          </motion.div>
        </Badge>

        {/* Enhanced Image Section */}
        <Box sx={{ position: "relative", height: 160 }}>
          {!imageError && data?.image_url ? (
            <CardMedia
              component="img"
              height={160}
              image={data.image_url}
              alt={data?.item_name || "Unlabeled Box"}
              onError={() => setImageError(true)}
              sx={{
                objectFit: "cover",
                filter:
                  theme.palette.mode === "dark"
                    ? "brightness(0.8)"
                    : "brightness(1)",
                transition: "filter 0.3s ease",
              }}
            />
          ) : (
            <Box
              sx={{
                height: 160,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: config.gradient,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Pattern Background */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  fontSize: "4rem",
                  opacity: 0.1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {config.bgPattern}
              </Box>

              <IconComponent
                sx={{
                  fontSize: 64,
                  color: "white",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                  zIndex: 1,
                }}
              />
            </Box>
          )}

          {/* Overlay Gradient */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "50%",
              background: `linear-gradient(to top, ${config.color}40, transparent)`,
            }}
          />
        </Box>

        {/* Enhanced Content Section */}
        <CardContent
          className="card-content"
          sx={{
            p: 3,
            position: "relative",
            zIndex: 2,
            transition: "background 0.3s ease",
          }}
        >
          {/* Header Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconComponent
                sx={{
                  fontSize: 20,
                  color: config.color,
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: isSmallScreen ? "1rem" : "1.1rem",
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(45deg, ${theme.palette.text.primary}, ${config.color})`
                      : `linear-gradient(45deg, ${theme.palette.text.primary}, ${config.color})`,
                  WebkitBackgroundClip: "text",
                  lineHeight: 1.2,
                }}
              >
                {data?.item_name || "Unlabeled Box"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOn sx={{ fontSize: 14, color: config.color }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  Box #{data?.box_no}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Schedule sx={{ fontSize: 14, color: config.color }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {getLastModified()}
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, opacity: 0.8 }}
            >
              {config.category}
            </Typography>
          </Box>

          {/* Fill Level Section */}
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Fill Level
              </Typography>
              <Chip
                label={status.label}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor: `${status.color}15`,
                  color: status.color,
                  border: `1px solid ${status.color}30`,
                }}
              />
            </Box>

            {/* Enhanced Progress Bar */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {data?.box_filled || 0} / {data?.box_capacity || 0}{" "}
                  {data?.box_unit || "units"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: status.color,
                    fontWeight: 700,
                  }}
                >
                  {filledPercentage}%
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  overflow: "hidden",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                  position: "relative",
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${filledPercentage}%` }}
                  transition={{ duration: 1, delay: 0.3 }}
                  style={{
                    height: "100%",
                    background: config.gradient,
                    borderRadius: 6,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Animated shimmer effect */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
                      animation: `shimmer 2s infinite`,
                      "@keyframes shimmer": {
                        "0%": { transform: "translateX(-100%)" },
                        "100%": { transform: "translateX(100%)" },
                      },
                    }}
                  />
                </motion.div>
              </Paper>
            </Box>

            {/* Status Message */}
            <Typography
              variant="caption"
              sx={{
                color: status.color,
                fontWeight: 600,
                opacity: 0.9,
                fontStyle: "italic",
              }}
            >
              {status.message}
            </Typography>

            {/* Action Button */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={onClick}
                startIcon={
                  status.severity === "error" ||
                  status.severity === "warning" ? (
                    <Add />
                  ) : (
                    <Scale />
                  )
                }
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  background:
                    status.severity === "error" || status.severity === "warning"
                      ? `linear-gradient(45deg, ${status.color}, ${status.color}dd)`
                      : `linear-gradient(45deg, ${config.color}, ${config.color}dd)`,
                  boxShadow: `0 4px 16px ${
                    status.severity === "error" || status.severity === "warning"
                      ? status.color
                      : config.color
                  }40`,
                  "&:hover": {
                    background:
                      status.severity === "error" ||
                      status.severity === "warning"
                        ? `linear-gradient(45deg, ${status.color}dd, ${status.color}bb)`
                        : `linear-gradient(45deg, ${config.color}dd, ${config.color}bb)`,
                    boxShadow: `0 8px 24px ${
                      status.severity === "error" ||
                      status.severity === "warning"
                        ? status.color
                        : config.color
                    }60`,
                  },
                }}
              >
                {status.severity === "error" || status.severity === "warning"
                  ? "Refill Now"
                  : "Manage Stock"}
              </Button>
            </motion.div>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PantryBox;
